const RELOAD_AT_STORAGE_KEY = "mela:chunk-reload-at";
const RELOAD_COOLDOWN_MS = 10_000;

const CHUNK_LOAD_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
  /Loading (CSS )?chunk [\w-]+ failed/i,
];

let reloadPending = false;

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (name === "ChunkLoadError") return true;
  return (
    typeof message === "string" &&
    CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message))
  );
};

export const isChunkReloadPending = () => reloadPending;

const readLastReloadAt = () => {
  try {
    return Number(sessionStorage.getItem(RELOAD_AT_STORAGE_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeLastReloadAt = (value: number) => {
  try {
    sessionStorage.setItem(RELOAD_AT_STORAGE_KEY, String(value));
  } catch {
    // Without storage the cooldown cannot survive the reload, so a chunk that is
    // still missing afterwards would reload forever. Refusing is the safe side.
    return false;
  }
  return true;
};

/**
 * A missing chunk almost always means a deploy replaced the assets referenced
 * by the index.html this tab was opened with; a reload picks up the new hashes.
 * The cooldown is stored in sessionStorage so a chunk that is still missing
 * after the reload surfaces as an error instead of a reload loop.
 *
 * @returns whether a reload was started (or is already in progress).
 */
export const reloadAfterChunkError = (): boolean => {
  if (reloadPending) return true;
  const now = Date.now();
  if (now - readLastReloadAt() < RELOAD_COOLDOWN_MS) return false;
  if (!writeLastReloadAt(now)) return false;
  reloadPending = true;
  window.location.reload();
  return true;
};

export const registerChunkPreloadErrorReload = () => {
  window.addEventListener("vite:preloadError", () => {
    reloadAfterChunkError();
  });
};

export const resetChunkReloadStateForTests = () => {
  reloadPending = false;
};
